/* eslint-disable react/require-default-props */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';

import AsyncSelect from 'react-select/async';
import { components } from 'react-select';
import { useDispatch } from 'react-redux';

const BaseFilter = ({
  value = [],
  onChange = () => {},
  request,
  requestQueryParams,
  labelSelector = (x) => x,
  valueSelector = (x) => x,
  preloadOptions = false,
}) => {
  const dispatch = useDispatch();

  const [defaultOptions, setDefaultOptions] = useState([]);
  const [hasLoadedInitialOptions, setHasLoadedInitialOptions] = useState(preloadOptions);
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const defaultOptionsRef = useRef([]);
  const selectRef = useRef(null);
  const timeoutRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Update ref when defaultOptions changes
  useEffect(() => {
    defaultOptionsRef.current = defaultOptions;
  }, [defaultOptions]);

  // Cleanup timeout and abort any pending requests on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const loadOptions = useCallback((inputValue, callback) => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Show loading immediately when user types (before debounce)
    setIsLoading(true);

    // Normalize the input value - treat null, undefined, or whitespace-only as empty
    const normalizedQuery = (inputValue || '').trim();

    // Set up debounced API call for all search queries (including empty string)
    timeoutRef.current = setTimeout(() => {
      // Create new AbortController for this request
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      dispatch(request({ query: normalizedQuery, ...requestQueryParams })).then(
        ({ data: results }) => {
          // Only process results if this request wasn't aborted
          if (!abortController.signal.aborted) {
            const options = results.values.map((r) => ({
              value: valueSelector(r),
              label: labelSelector(r),
            }));
            
            // Update default options cache if this was an empty query
            if (!normalizedQuery) {
              setDefaultOptions(options);
              if (!preloadOptions && !hasLoadedInitialOptions) {
                setHasLoadedInitialOptions(true);
              }
            }
            
            setIsLoading(false);
            callback(options);
          }
        }
      ).catch((error) => {
        // Only handle error if this request wasn't aborted
        if (!abortController.signal.aborted) {
          setIsLoading(false);
          callback([]);
        }
      });
    }, 750);
  }, [dispatch, request, requestQueryParams, labelSelector, valueSelector, preloadOptions, hasLoadedInitialOptions]);

  useEffect(() => {
    // Only load default options on mount if preloadOptions is true
    if (preloadOptions) {
      setIsLoading(true);
      
      // Create AbortController for this request
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      
      dispatch(request({ query: '', ...requestQueryParams })).then(
        ({ data: results }) => {
          if (!abortController.signal.aborted) {
            const options = results.values.map((r) => ({
              value: valueSelector(r),
              label: labelSelector(r),
            }));
            setDefaultOptions(options);
            setIsLoading(false);
          }
        }
      ).catch(() => {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preloadOptions]);

  const handleMenuOpen = useCallback(() => {
    setMenuIsOpen(true);
    // For non-preloading filters, load options when menu opens for the first time
    if (!preloadOptions && !hasLoadedInitialOptions && defaultOptionsRef.current.length === 0) {
      setHasLoadedInitialOptions(true);
      setIsLoading(true);
      
      // Cancel any pending request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new AbortController for this request
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      
      // Directly make the API call and update the options
      dispatch(request({ query: '', ...requestQueryParams })).then(
        ({ data: results }) => {
          if (!abortController.signal.aborted) {
            const options = results.values.map((r) => ({
              value: valueSelector(r),
              label: labelSelector(r),
            }));
            setDefaultOptions(options);
            setIsLoading(false);
          }
        }
      ).catch(() => {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      });
    }
  }, [preloadOptions, hasLoadedInitialOptions, dispatch, request, requestQueryParams, labelSelector, valueSelector]);

  const handleMenuClose = useCallback(() => {
    setMenuIsOpen(false);
  }, []);

  const CustomMenuList = (props) => (
    <components.MenuList {...props}>
      <div style={{ padding: '8px 12px', color: '#888', fontSize: '0.85em', borderBottom: '1px solid #eee'}}>
        <span>Showing up to 100 results. Enter a search term to refine these results.</span>
      </div>
      {props.children}
    </components.MenuList>
  );

  return (
    <AsyncSelect
      ref={selectRef}
      value={value}
      defaultOptions={preloadOptions ? defaultOptions : (defaultOptions.length > 0 ? defaultOptions : false)}
      menuIsOpen={menuIsOpen}
      className="filter"
      onChange={onChange}
      isMulti
      isLoading={isLoading}
      loadOptions={loadOptions}
      onMenuOpen={handleMenuOpen}
      onMenuClose={handleMenuClose}
      cacheOptions={false}
      components={{ MenuList: CustomMenuList }}
      noOptionsMessage={({ inputValue }) =>
        inputValue ? 'No matches' : 'Start typing to see matches'
      }
    />
  );
};

BaseFilter.propTypes = {
  onChange: PropTypes.func,
  request: PropTypes.func.isRequired,
  requestQueryParams: PropTypes.objectOf(PropTypes.shape),
  labelSelector: PropTypes.func,
  valueSelector: PropTypes.func,
  preloadOptions: PropTypes.bool,
  value: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({ label: PropTypes.string, value: PropTypes.string })
    ),
    PropTypes.shape({ label: PropTypes.string, value: PropTypes.string }),
  ]),
};

export default BaseFilter;
